/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useState, useMemo, useRef, useEffect } from "react";
import { string, arrayOf, shape } from "prop-types";
import cx from "classnames";
import uniqueId from "lodash.uniqueid";
import debounce from "lodash.debounce";
import slugify from "slugify";

import Icon from "../../../../Common/js/components/Icon/Icon.jsx";
import SectionHeadline from "../../../../Common/js/components/SectionHeadline/SectionHeadline.jsx";

const propTypes = {
  title: string.isRequired,
  description: string.isRequired,
  items: arrayOf(
    shape({
      title: string.isRequired,
      date: string.isRequired,
      description: string.isRequired,
    }),
  ),
};

const Timeline = (props) => {
  const { title, description, items } = props;

  const [activeItem, setActiveItem] = useState(0);
  const [componentWidth, setComponentWidth] = useState();
  const controlsId = useMemo(
    () => uniqueId(`${slugify(title).toLowerCase()}-`),
    [title],
  );

  const skipEl = useRef();
  const itemsContainer = useRef();

  useEffect(() => {
    const resize = debounce(
      () => setComponentWidth(itemsContainer.current.clientWidth),
      300,
    );

    if (itemsContainer.current) {
      setComponentWidth(itemsContainer.current.clientWidth);
      window.addEventListener("resize", resize);
      //setComponentWidth(itemsContainer.current.clientWidth);
    }
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [itemsContainer]);

  //determine if the collapsed card should be shown
  const isVisible = (index, array) => {
    //if the active card is the first card then display the following 4
    if (activeItem === 0) {
      switch (index) {
        case 0:
        case 1:
        case 2:
        case 3:
        case 4:
          return true;
        default:
          return false;
      }
    }
    //if the active card is the second card then display the first and the following 3
    else if (activeItem === 1) {
      switch (index) {
        case 0:
        case 1:
        case 2:
        case 3:
        case 4:
          return true;
        default:
          return false;
      }
    }
    //if the active card is the last card then display the previous 4
    else if (activeItem === array.length - 1) {
      switch (index) {
        case array.length - 1:
        case array.length - 2:
        case array.length - 3:
        case array.length - 4:
        case array.length - 5:
          return true;
        default:
          return false;
      }
    }
    //if the active card is the second to last then display the last card and the previous three
    else if (activeItem === array.length - 2) {
      switch (index) {
        case array.length - 1:
        case array.length - 2:
        case array.length - 3:
        case array.length - 4:
        case array.length - 5:
          return true;
        default:
          return false;
      }
    }
    //if the active card is not the first or the last then display the following two and previous two
    else {
      switch (index) {
        case activeItem - 1:
        case activeItem - 2:
        case activeItem:
        case activeItem + 1:
        case activeItem + 2:
          return true;
      }
    }
  };

  return (
    <section className="timeline component">
      {title && <SectionHeadline title={title} subtitle={description} />}
      <div className="timeline__skip-container">
        <button
          type="button"
          className="btn timeline__skip u-visuallyhidden"
          onClick={(e) => skipEl.current.focus()}
        >
          Skip Component
        </button>
      </div>
      <div
        id={controlsId}
        ref={itemsContainer}
        className="timeline__items-container"
      >
        {items &&
          items.map((item, i, arr) =>
            isVisible(i, arr) ? (
              <div
                aria-label={item.title}
                className={cx("timeline__item", {
                  "timeline__item--active": activeItem === i,
                })}
                key={i}
                id={`${controlsId}-item-${i}`}
              >
                {item.title && (
                  <button
                    className="timeline__item-toggle btn"
                    type="button"
                    onClick={(e) => setActiveItem(i)}
                    aria-expanded={i === activeItem ? true : false}
                  >
                    <h3 className="timeline__toggle-text">
                      <span>{i + 1}. </span>
                      {i === activeItem && (
                        <span className="timeline__item-title">
                          {item.title}
                        </span>
                      )}
                    </h3>
                  </button>
                )}
                {i === activeItem && (
                  <div
                    aria-hidden={i === activeItem ? false : true}
                    className="timeline__item-inner"
                  >
                    {item.date && <div>{item.date}</div>}
                    {item.description && (
                      <div
                        dangerouslySetInnerHTML={{ __html: item.description }}
                        className="timeline__item-description"
                      ></div>
                    )}
                  </div>
                )}
              </div>
            ) : null,
          )}
      </div>
      <div
        style={{ maxWidth: `${componentWidth}px` }}
        aria-controls={`#${controlsId}`}
        className="timeline__controls"
      >
        <button
          className="btn timeline__previous"
          onClick={(e) => {
            activeItem === 0
              ? setActiveItem(items.length - 1)
              : setActiveItem(activeItem - 1);
          }}
          disabled={activeItem === 0}
          type="button"
          aria-label="previous item"
        >
          <span className="u-visuallyhidden">Previous Item</span>
          <Icon iconName="arrow-left" className="icon--36x36" />
        </button>
        <button
          onClick={(e) => {
            activeItem === items.length - 1
              ? setActiveItem(0)
              : setActiveItem(activeItem + 1);
          }}
          disabled={activeItem === items.length - 1}
          className="btn timeline__next"
          type="button"
          aria-label="next item"
        >
          <span className="u-visuallyhidden">Next Item</span>
          <Icon iconName="arrow-right" className="icon--36x36" />
        </button>
      </div>
      <div className="timeline__skip-container">
        <a
          ref={skipEl}
          tabIndex="-1"
          className="timeline__skip u-visuallyhidden"
        >
          End of {title}
        </a>
      </div>
    </section>
  );
};

Timeline.propTypes = propTypes;
export default Timeline;
